import { getPagination } from "@selectors";
import { getFormValues } from "redux-form";
import { USER_TYPES } from "@modules/account/constants";
import type { State } from "@store/reducers";

import type { PaginationExtendedType } from "@types";

export const getError = (state: State) => (state.employees || {}).error;

export const getSuccessMessage = (state: State) => (state.employees || {}).success;

export const isLoading = (state: State) => {
  const employeeLoading = ((state.employees || {}).employee || {}).requestsInProgress > 0;
  const personalDetailsLoading =
    (((state.employees || {}).employee || {}).personalDetails || {}).requestsInProgress > 0;
  const medicalDetailsLoading =
    (((state.employees || {}).employee || {}).medicalDetails || {}).requestsInProgress > 0;
  const employmentDetailsLoading =
    (((state.employees || {}).employee || {}).employmentDetails || {}).requestsInProgress > 0;
  const absencesLoading = ((state.employees || {}).absences || {}).requestsInProgress > 0;
  const userAccountLoading =
    (((state.employees || {}).employee || {}).userAccount || {}).requestsInProgress > 0;

  return (
    employeeLoading ||
    personalDetailsLoading ||
    medicalDetailsLoading ||
    employmentDetailsLoading ||
    absencesLoading ||
    userAccountLoading
  );
};

// Vaccination
export const getAddVaccinationModal = (state: Object) => {
  return state.employees.employee.medicalDetails.modals.addVaccination;
};
export const getDeleteVaccinationModal = (state: Object) => {
  return state.employees.employee.medicalDetails.modals.deleteVaccination;
};
export const getEmployeeVaccinations = (state: Object) => {
  return state.employees.employee.medicalDetails.vaccinations;
};

// Employee search
export const getActiveClientsOptions = (state: State) => {
  const clients = state.employees.clients.list.content || [];

  return clients.map(item => ({
    value: item.client.id,
    label: item.client.name,
  }));
};

export const isActiveClientsLoading = (state: State) =>
  state.employees.clients.requestsInProgress > 0;

export const getEmployeeName = (employee = {}) => `${employee?.firstName} ${employee?.lastName}`;

export const getEmployees = (state: State) =>
  (((state.employees || {}).employee || {}).list || {}).content || [];

export const getEmployee = (employeeId: number) => (state: Object) => {
  return (
    state.employees.employee.list &&
    state.employees.employee.list.content.find(emplyee => emplyee.id === employeeId)
  );
};

export const employeeReferralHistory = (state: State) => {
  return state.employees.employee.referralHistory;
};

export const getCurrentEmployee = (state: Object) => {
  return state.employees?.employee?.current;
};
export const getCurrentEmployeeAccessLevel = (state: Object): string => {
  return getCurrentEmployee(state)?.accessLevel || "";
};

export const getEmployeeFormData = employeeId => (state: State) => {
  const employees = getEmployees(state);
  const employee = employees.find(item => item.id === employeeId);

  return employee
    ? {
        firstName: employee.firstName,
        lastName: employee.lastName,
        employeeRefPrimary: employee.employeeRefPrimary,
        employeeRefSecondary: employee.employeeRefSecondary,
        dateOfBirth: employee.dateOfBirth,
        gender: employee.gender,
        isActive: employee.isActive,
        isNonServiceUser: employee.isNonServiceUser,
        hasHardOfHearing: employee.hasHardOfHearing,
        hasDisability: employee.hasDisability,
        employmentTypeId: (employee.employmentType || {}).id,
        joinDate: employee.joinDate,
        leaveDate: employee.leaveDate,
        id: employee.id,
        homeLandline: employee.contactDetails.homeLandline,
        homeMobile: employee.contactDetails.homeMobile,
        notificationMobile: employee.contactDetails.notificationMobile,
        workEmail: employee.contactDetails.workEmail,
        homeEmail: employee.contactDetails.homeEmail,
        question: (employee.securityQuestion || {}).question,
        answer: (employee.securityQuestion || {}).answer,
      }
    : {};
};

export const getEmployeesSort = (state: State) =>
  (((((state.employees || {}).employee || {}).list || {}).pagination || {}).sort || {}).orders;

export const getEmployeesPagination = (state: State): PaginationExtendedType =>
  getPagination(state.employees.employee.list);

export const getEmployeeFilters = (state: State) => getFormValues("employee-filter")(state) || {};

export const getEmployeeNameSearchform = state => {
  return getFormValues("employee-name-searchform")(state) || "";
};

export const getEmployeeExtendedFilters = state => {
  return getFormValues("client-add-employee-step-1")(state) || {};
};

export const getEmployeeFilterInitialValues = (state: State) =>
  ((state.employees || {}).employee || {}).employeeFilterInitialValues;

export const getIsAddEmployeeModalVisible = (state: State) =>
  state.employees.employee.modals.addEmployee.visible;

export const getAddEmployee = (state: State) =>
  state.employees.employee.modals.addEmployee.employee;

export const getIsAddEmployeeChooseClientModalVisible = (state: State) =>
  ((state.employees.employee.modals || {}).addEmployeeChooseClient || {}).visible;

export const getClientId = (state: State) => state.employees.employee.clientId;

export const getTotalEmployees = (state: State) => state.employees.employee.list.totalElements || 0;

//Personal Details
export const getActionSummary = (state: Object) => {
  return state.employees.employee.actionSummary;
};

export const getChangelog = (state: State) =>
  state?.employees?.employee?.changelog?.list?.content || [];

export const getChangelogSort = (state: State) =>
  (
    (((((state.employees || {}).employee || {}).changelog || {}).list || {}).pagination || {})
      .sort || {}
  ).orders;

export const getChangelogPagination = (state: State): PaginationExtendedType =>
  getPagination(state.employees.employee.changelog.list);

export const getEmployeeSchema = (state: State) => state.employees.employee.schema;

export const getAccessLeveSchema = (state: State) => state.employees.employee.accessLeveSchema;

export const getAccessLevelValue = (state: State) => state.employees.employee.accessLevel; // That is working strange... it should search for accessLevel in  state.employees.employee.current.accessLevel

export const getAddEmployeeSchema = (state: State) => state.employees.employee.addEmployeeSchema;

// Employment Details
export const getRoleSummary = (state: State) =>
  state.employees.employee.employmentDetails.roleSummary.content;

export const getRoleSummaryPagination = (state: State): PaginationExtendedType => {
  const roleSummary = state.employees.employee.employmentDetails.roleSummary;
  return getPagination(roleSummary);
};

export const getEmployeePositionSchema = (state: State) =>
  state.employees.employee.employmentDetails.schema.employeePosition;

export const getIsAddEmployeePositionModalVisible = (state: State) =>
  state.employees.employee.employmentDetails.modals.addPosition.visible;

export const getIsEditEmployeePositionModalVisible = (state: State) =>
  state.employees.employee.employmentDetails.modals.editPosition.visible;

export const getEmployeePositionFormData = (state: State) =>
  state.employees.employee.employmentDetails.modals.editPosition.formData;

export const getEmploymentsSummary = employeeId => (state: State) => {
  const employees = getEmployees(state);
  const filteredEmployees = employees.filter(el => el.id === employeeId);

  return (filteredEmployees[0] || {}).employmentSummary;
};

export const getEmploymentSummaryChangelog = (state: State) =>
  state.employees.employee.employmentDetails.changelog.list.content || [];

// My Absences
export const getMyAbsences = (state: State) => state.employees.employee.myAbsences.absences.content;

export const getMyAbsencesPagination = (state: State) =>
  state.employees.employee.myAbsences.absences.pagination;

// Medical Details
export const getIsAddMedicationModalVisible = (state: State) =>
  state.employees.employee.medicalDetails.modals.addMedication.visible;

export const getIsAddAllergyModalVisible = (state: State) =>
  state.employees.employee.medicalDetails.modals.addAllergy.visible;

export const getIsAddMedicalHistoryItemModalVisible = (state: State) =>
  state.employees.employee.medicalDetails.modals.addMedicalHistoryItem.visible;

export const getIsRemoveMedicationModalVisible = (state: State) =>
  state.employees.employee.medicalDetails.modals.removeMedication.visible;

export const getIsRemoveAllergyModalVisible = (state: State) =>
  state.employees.employee.medicalDetails.modals.removeAllergy.visible;

export const getIsRemoveMedicalHistoryItemModalVisible = (state: State) =>
  state.employees.employee.medicalDetails.modals.removeMedicalHistoryItem.visible;

export const getEmployeeMedications = (state: State) =>
  state.employees.employee.medicalDetails.medications.content;

export const getEmployeeAllergies = (state: State) =>
  state.employees.employee.medicalDetails.allergies.content;

export const getEmployeeMedicalHistory = (state: State) =>
  state.employees.employee.medicalDetails.medicalHistory.content;

export const getMedicationSchema = (state: State) =>
  state.employees.employee.medicalDetails.schema.medication;

export const getAllergySchema = (state: State) =>
  state.employees.employee.medicalDetails.schema.allergy;

export const getMedicalHistorySchema = (state: State) =>
  state.employees.employee.medicalDetails.schema.medicalHistory;

export const getMedicationId = (state: State) =>
  state.employees.employee.medicalDetails.modals.removeMedication.medicationId;

export const getAllergyId = (state: State) =>
  state.employees.employee.medicalDetails.modals.removeAllergy.allergyId;

export const getMedicalHistoryItemId = (state: State) =>
  state.employees.employee.medicalDetails.modals.removeMedicalHistoryItem.medicalHistoryItemId;

export const getMedicalDetailsChangelog = (state: State) =>
  state.employees.employee.medicalDetails.changelog.list.content || [];

export const getMedicalDetailsChangelogPagination = (state: State): PaginationExtendedType =>
  getPagination(state.employees.employee.medicalDetails.changelog.list);

export const getMedicationsPagination = (state: State): PaginationExtendedType =>
  getPagination(state.employees.employee.medicalDetails.medications);

export const getMedicalHistoryPagination = (state: State): PaginationExtendedType =>
  getPagination(state.employees.employee.medicalDetails.medicalHistory);

export const getMedicalHistoryItemName = itemId => (state: State) => {
  const history = getEmployeeMedicalHistory(state) || [];
  const entry = history.find(el => el.id === itemId);

  return !(entry || {}).condition
    ? ((entry | {}).treatment || {}).name
    : ((entry || {}).condition || {}).name;
};

export const getMedicationItemName = itemId => (state: State) => {
  const medications = getEmployeeMedications(state) || [];
  const filteredMedications = medications.filter(el => el.id === itemId);

  return ((filteredMedications[0] || {}).medication || {}).name;
};

export const getAllergyItemName = itemId => (state: State) => {
  const allergies = getEmployeeAllergies(state) || [];
  const filteredAllergies = allergies.filter(el => el.id === itemId);

  return ((filteredAllergies[0] || {}).allergy || {}).name;
};

export const getEmployeeMedicationSort = (state: State) =>
  (state.employees.employee.medicalDetails.medications.pagination.sort || {}).orders;

export const getEmployeeAllergySort = (state: State) =>
  (state.employees.employee.medicalDetails.allergies.pagination.sort || {}).orders;

export const getEmployeeMedicalHistorySort = (state: State) =>
  (state.employees.employee.medicalDetails.medicalHistory.pagination.sort || {}).orders;

export const getContactInfoModalVisible = (state: State) =>
  state.employees.employee.modals.contactInfo.visible;

export const getHomePageSettingsModalVisible = (state: State) =>
  state.employees.employee.modals.homePageSettings.visible;

export const getChangePasswordModalVisible = (state: State) =>
  state.employees.employee.modals.changePassword.visible;

export const getAbsenceDetailsModalVisible = (state: State) =>
  state.employees.employee.modals.absenceDetails.visible;

// User Account Details
export const getIsEditAccountDetailsModalVisible = (state: State) =>
  state.employees.employee.userAccount.modals.editAccountDetails.visible;

export const getIsEditResetPasswordModalVisible = (state: State) =>
  state.employees.employee.userAccount.modals.editResetPassword.visible;

export const getIsConfirmResetPasswordModalVisible = (state: State) =>
  state.employees.employee.userAccount.modals.confirmResetPassword.visible;

export const getIsEditAreaAccessModalVisible = (state: State) =>
  state.employees.employee.userAccount.modals.editAreaAccess.visible;

export const getImpersonateModal = (state: State) =>
  state.employees.employee.userAccount.modals.impersonate;

export const getUserAccountAssignments = (state: State) =>
  state.employees.employee.userAccount.assignments;

export const getUserRecentLogins = (state: State) =>
  state.employees.employee.userAccount.recentLogins;

export const getUserAccountChangelog = (state: State) =>
  state.employees.employee.userAccount.changelog.content;

export const getUserAccount = (state: State) => state.employees.employee.userAccount.user;

// My Details
export const getMyDetails = (state: State) => state.employees.employee.myDetails.data || null;

export const getContactInfoSchema = (state: State) =>
  state.employees.employee.myDetails.contactInfoSchema || null;

export const getIsExcludeEmployeeFromAlertsModalOpen = (state: State) =>
  state.employees.employee.modals.addAlertExclusion.visible;

export const getAlertExclusionModalStep = (state: State) =>
  state.employees.employee.modals.addAlertExclusion.step;

export const getEmployeeAlertExclusions = (state: State) =>
  (state.employees.employee.employeeAlertExclusions || {}).content;

export const showRemoveAlertExclusionModal = (state: State) =>
  state.employees.employee.modals.removeAlertExclusion.visible;

export const showEditAlertExclusionModal = (state: State) =>
  state.employees.employee.modals.editAlertExclusion.visible;

export const getExclusionAlertName = (state: State) =>
  state.employees.employee.modals.removeAlertExclusion.alertName;

export const getEmployeeAlertExclusionId = (state: State) =>
  state.employees.employee.modals.employeeAlertExclusionId;

export const getEmployeeAlertExclusionSchema = (state: State) =>
  state.employees.employee.employeeAlertExclusionSchema;

export const getAlertExclusionAlertsOptions = (state: State) => {
  const employeeExclusionReasons =
    (state.employees.employee.modals.alert || {}).employeeExclusionReasons || [];

  return employeeExclusionReasons.map(item => ({ const: item.id, title: item.reason }));
};

export const getClientAlertId = (state: State) => (state.employees.employee.modals.alert || {}).id;

export const getExclusionReasonDetails = (state: State) =>
  state.employees.employee.modals.exclusionReasonDetails;

export const getClientAlertsFromSchemaResolve = (state: State) =>
  (
    (
      (
        ((state.employees.employee.employeeAlertExclusionSchema || {}).properties || {})
          .alertConfiguration || {}
      ).properties || {}
    ).id || {}
  ).oneOf;

export const getEmployeeDraft = state => {
  return state.employees.employee.draft;
};

export const getEmployeeAccountLockedStatus = state => {
  const recentLogins = state.employees.employee.userAccount.recentLogins;

  return (recentLogins.length && recentLogins[0].status === "BLOCKED") || false;
};

export const isAccountActive = (state: State): boolean => {
  const user = state.employees.employee.userAccount.user;
  const userType = user && user.userType;

  if (!userType) {
    return false;
  }

  return true;
};

export const showEmployeeAlertExclusions = (state): boolean =>
  (((state.settings.settings || {}).serviceSettings || {}).userPermissions || {})
    .excludeAbsencesFromAlerts || false;

export const getScheduledReportLogDataModalData = state => {
  return state.employees.employee.userAccount.modals.logData.data;
};
export const getScheduledReportLogDataModalVisible = state => {
  return state.employees.employee.userAccount.modals.logData.visible;
};
